import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  Icon,
  Input,
  Select,
  CheckBox,
  Message,
  Modal,
} from '@iwoca/orion';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { TSupportedIntroducerTypes } from 'lendingApi/lendingApi.types';
import styles from './RegistrationForm.module.css';
import { useRegisterIntroducerMutation } from './registrationHelper';

type RegistrationFormDataUK = {
  // Company information
  introducerType: TSupportedIntroducerTypes;
  companyName: string;
  FCAReferenceNumber?: string;
  companyNumber?: string;
  addressLine1: string;
  addressLine2?: string;
  town: string;
  postcode: string;
  companyWebsite?: string;
  // Personal information.
  title: 'Mr' | 'Mrs';
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  privacyPolicyAgreement: boolean;
};

const introducerTypes: TSupportedIntroducerTypes[] = ['broker', 'accountant'];

const personalInformationSchema = yup.object().shape({
  title: yup.string().required(),
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup
    .string()
    .email('Please use a valid email format')
    .required('Email address is required'),
  phoneNumber: yup.string().required('Phone number is required'),
  privacyPolicyAgreement: yup
    .boolean()
    .required('Privacy policy is required')
    .oneOf([true], 'The terms and conditions must be accepted.'),
});

const fullRegistrationSchema = personalInformationSchema.shape({
  introducerType: yup.string().required().oneOf(introducerTypes),
  companyName: yup.string().required('Company name is required'),
  FCAReferenceNumber: yup.string(),
  companyNumber: yup.string(),
  addressLine1: yup.string().required('Address line 1 is required'),
  addressLine2: yup.string(),
  town: yup.string().required('Town / city is required'),
  postcode: yup.string().required('Company postal code is required'),
  companyWebsite: yup.string(),
});

export const RegistrationFormUK = () => {
  const router = useRouter();
  const inviteId = router.query.introducer as string | undefined;
  const introducerTypeFromQueryParameter =
    router.query.introducerType === 'accountant' ? 'accountant' : 'broker';
  const [introducerType, setIntroducerType] =
    useState<TSupportedIntroducerTypes>();
  const [showErrorModal, setShowErrorModal] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegistrationFormDataUK>({
    defaultValues: {
      introducerType: introducerTypeFromQueryParameter,
    },
    resolver: yupResolver(
      inviteId ? personalInformationSchema : fullRegistrationSchema,
    ),
  });

  useEffect(() => {
    if (introducerTypeFromQueryParameter) {
      setIntroducerType(introducerTypeFromQueryParameter);
    }
  }, [introducerTypeFromQueryParameter]);

  const signUpMutation = useRegisterIntroducerMutation({
    country: 'UK',
    onError: () => setShowErrorModal(true),
  });

  const renderCompanyInfo = () => {
    return (
      <>
        <h2>Company Information</h2>
        <Card className={styles.companyInfoContainer}>
          <div className={styles.formField}>
            <Select
              {...register('introducerType')}
              id="introducerType"
              name="introducerType"
              placeholder="Choose an option"
              onChange={(e) =>
                // Can cast this because we only have the options below
                setIntroducerType(e.target.value as TSupportedIntroducerTypes)
              }
              className={styles.select}
              label="Introducer type"
              error={errors.introducerType && errors.introducerType.message}
            >
              <option value="accountant">Accounting / advisory firm</option>
              <option value="broker">Brokerage</option>
            </Select>
          </div>
          <div className={styles.formField}>
            <Input
              {...register('companyName')}
              id="companyName"
              name="companyName"
              type="text"
              label="Company name"
              placeholder="Iwoca"
              error={errors.companyName && errors.companyName.message}
            />
          </div>
          {introducerType === 'broker' && (
            <div className={styles.formField}>
              <Input
                {...register('FCAReferenceNumber')}
                id="FCAReferenceNumber"
                name="FCAReferenceNumber"
                type="text"
                label="FCA reference number (optional)"
                placeholder="123456"
              />
            </div>
          )}
          <div className={styles.formField}>
            <Input
              {...register('companyNumber')}
              id="companyNumber"
              name="companyNumber"
              type="text"
              label="Companies House number (optional)"
              placeholder="12345678"
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('addressLine1')}
              id="addressLine1"
              name="addressLine1"
              type="text"
              label="Address line 1"
              placeholder="2 Street"
              error={errors.addressLine1 && errors.addressLine1.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('addressLine2')}
              id="addressLine2"
              name="addressLine2"
              type="text"
              label="Address line 2 (optional)"
              placeholder="Unit 12"
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('town')}
              id="town"
              name="town"
              type="text"
              label="Town / city"
              placeholder="London"
              error={errors.town && errors.town.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('postcode', {
                setValueAs: (text: string) => text.toUpperCase(),
              })}
              id="postcode"
              name="postcode"
              type="text"
              label="Company postcode"
              placeholder="W1W 6XH"
              error={errors?.postcode?.message && errors.postcode.message}
              className={styles.uppercaseText}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('companyWebsite')}
              id="companyWebsite"
              name="companyWebsite"
              type="text"
              label="Company website (optional)"
              placeholder="www.iwoca.co.uk"
            />
          </div>
        </Card>
      </>
    );
  };

  const renderAccountInfo = () => {
    return (
      <>
        <h2>Account Information</h2>
        <Card className={styles.accountInformationContainer}>
          <div className={styles.formField}>
            <Select
              {...register('title')}
              label="Title"
              id="title"
              name="title"
              type="text"
            >
              <option value="Mr">Mr</option>
              <option value="Ms">Ms</option>
              <option value="Mrs">Mrs</option>
              <option value="Miss">Miss</option>
            </Select>
          </div>
          <div className={styles.formField}>
            <Input
              {...register('firstName')}
              id="firstName"
              name="firstName"
              type="text"
              label="First Name"
              placeholder="John"
              error={errors.firstName && errors.firstName.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('lastName')}
              id="lastName"
              name="lastName"
              type="text"
              label="Last Name"
              placeholder="Smith"
              error={errors.lastName && errors.lastName.message}
            />
          </div>
          <div className={clsx(styles.formField, styles.email)}>
            <Input
              {...register('email')}
              id="email"
              name="email"
              type="text"
              label="Email address"
              placeholder="me@iwoca.co.uk"
              error={errors.email && errors.email.message}
            />
          </div>
          <div className={styles.formField}>
            <Input
              {...register('phoneNumber')}
              id="phoneNumber"
              name="phoneNumber"
              type="text"
              label="Phone Number"
              placeholder="+44 123456789"
              error={errors.phoneNumber && errors.phoneNumber.message}
            />
          </div>
        </Card>
      </>
    );
  };

  return (
    <div className={styles.container}>
      <h1>Sign up to create an introducer account</h1>
      {!inviteId && (
        <p className={styles.inviteInformation}>
          Is your company already partnered with us? Get a colleague to share a
          referral link to get started straight away.
        </p>
      )}
      <form
        className={styles.form}
        onSubmit={handleSubmit((values) => signUpMutation.mutate(values))}
      >
        {!inviteId && renderCompanyInfo()}
        {renderAccountInfo()}
        <div className={styles.privacyPolicyAgreement}>
          <CheckBox
            {...register('privacyPolicyAgreement')}
            id="privacyPolicyAgreement"
            name="privacyPolicyAgreement"
            data-testid="privacy-policy-agreement"
          />
          <div className={styles.privacyPolicyDescription}>
            <p>
              <strong>
                I agree to iwoca carrying out due diligence on the company I
                represent and understand that iwoca will contact me for
                signature on the iwoca introducer agreement.
              </strong>
            </p>
            <p>
              Your privacy is important to us at iwoca, and we’re committed to
              protecting your personal data. We’ll store your personal data
              securely, and we’ll only get in touch in line with our{' '}
              <a
                href="https://www.iwoca.co.uk/introducer-privacy-policy/"
                target="_blank"
                rel="noreferrer"
              >
                privacy policy
              </a>{' '}
              or if you opt in for extra communication from us.
            </p>
            {errors.privacyPolicyAgreement && (
              <Message
                message={errors.privacyPolicyAgreement.message || ''}
                type="error"
              />
            )}
          </div>
        </div>
        <div className={styles.footerItem}>
          <p className={styles.submitDescription}>
            We’ll process your registration.
          </p>
        </div>
        <div className={styles.footerItem}>
          <Button
            variant="primary"
            size="small"
            type="submit"
            loading={signUpMutation.isPending || signUpMutation.isSuccess}
          >
            Create an account
            <Icon icon="lockCloseFilled" />
          </Button>
        </div>
      </form>
      <Modal
        closeIcon
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="There's a problem with the info you've entered."
        className={styles.modal}
      >
        <p className={styles.modalContent}>
          If you have an account with iwoca, use the login button in the top
          right, otherwise give us a call and we’ll help you get logged in.
        </p>
      </Modal>
    </div>
  );
};
